/**
 * Role Scoping — Entity-level permission constraints
 *
 * Implements the scoped overrides described in the
 * "Forge permissions use role templates with scoped overrides" decision.
 *
 * When a user has a role with scope rows in `role_scopes`, their role
 * only applies to the specified entities. When no scope rows exist
 * for a role, it applies org-wide (backward compatible) -- this
 * extends to every module the role touches, including report export
 * (#4656: confirmed intentional, not accidental scope creep).
 *
 * Example: A "ministry_leader" role scoped to group "Young Adults"
 * can only manage people, events, comms, and report data within that
 * group.
 *
 * New scoped assignments should explicitly choose named entities or
 * acknowledge org-wide access (human decision, 2026-08-01, #4656) --
 * legacy unscoped roles remain org-wide but are surfaced for admin
 * review on Settings > Permissions for report-export-capable roles
 * (see report_export_scope_reviews / get_unreviewed_orgwide_report_roles
 * in supabase/migrations/20260811150000_report_export_scope_review.sql).
 */

import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";

// ---------------------------------------------------------------------------
// Types: unreviewed org-wide report-export access (#4656)
// ---------------------------------------------------------------------------

export interface UnreviewedOrgWideReportRole {
  user_id: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RoleScope {
  id: string;
  user_id: string;
  role: string;
  scope_entity_type: string;
  scope_entity_id: string;
  organization_id: string;
  created_at: string;
  created_by: string | null;
}

export type ScopeEntityType = "group" | "ministry" | "campus" | "fund" | "service";

// ---------------------------------------------------------------------------
// Hook: fetch scopes for the current user
// ---------------------------------------------------------------------------

export function useMyRoleScopes() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["role-scopes", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("role_scopes" as any)
        .select("*")
        .eq("user_id", user!.id);

      if (error) throw error;
      return (data ?? []) as unknown as RoleScope[];
    },
    staleTime: 5 * 60 * 1000, // cache for 5 minutes
  });
}

// ---------------------------------------------------------------------------
// Hook: fetch scopes for a specific user (admin view)
// ---------------------------------------------------------------------------

export function useUserRoleScopes(userId: string | undefined) {
  return useQuery({
    queryKey: ["role-scopes", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("role_scopes" as any)
        .select("*")
        .eq("user_id", userId!);

      if (error) throw error;
      return (data ?? []) as unknown as RoleScope[];
    },
  });
}

// ---------------------------------------------------------------------------
// Check: does a user's role apply to a specific entity?
// ---------------------------------------------------------------------------

/**
 * Determines whether a role is authorized for a specific entity.
 *
 * - If the user has NO scope rows for this role → org-wide access (returns true)
 * - If the user has scope rows → returns true only if the entity matches
 */
export function isRoleScopedToEntity(
  scopes: RoleScope[],
  role: string,
  organizationId: string,
  entityType: ScopeEntityType,
  entityId: string
): boolean {
  const roleScopes = scopes.filter(
    (s) => s.role === role && s.organization_id === organizationId
  );

  // No scopes for this role = org-wide access
  if (roleScopes.length === 0) return true;

  // Check if any scope matches this entity
  return roleScopes.some(
    (s) => s.scope_entity_type === entityType && s.scope_entity_id === entityId
  );
}

/**
 * Get all entity IDs a role is scoped to for a given entity type.
 * Returns null if the role is unscoped (org-wide).
 */
export function getScopedEntityIds(
  scopes: RoleScope[],
  role: string,
  organizationId: string,
  entityType: ScopeEntityType
): string[] | null {
  const roleScopes = scopes.filter(
    (s) => s.role === role && s.organization_id === organizationId
  );

  // No scopes = org-wide
  if (roleScopes.length === 0) return null;

  return roleScopes
    .filter((s) => s.scope_entity_type === entityType)
    .map((s) => s.scope_entity_id);
}

// ---------------------------------------------------------------------------
// Mutations: add/remove scopes
// ---------------------------------------------------------------------------

export async function addRoleScope(params: {
  userId: string;
  role: string;
  scopeEntityType: ScopeEntityType;
  scopeEntityId: string;
  organizationId: string;
}): Promise<RoleScope> {
  const { data, error } = await supabase
    .from("role_scopes" as any)
    .insert({
      user_id: params.userId,
      role: params.role,
      scope_entity_type: params.scopeEntityType,
      scope_entity_id: params.scopeEntityId,
      organization_id: params.organizationId,
    })
    .select()
    .single();

  if (error) throw error;
  return data as unknown as RoleScope;
}

export async function removeRoleScope(scopeId: string): Promise<void> {
  const { error } = await supabase
    .from("role_scopes" as any)
    .delete()
    .eq("id", scopeId);

  if (error) throw error;
}

export async function removeAllScopesForUserRole(
  userId: string,
  role: string
): Promise<void> {
  const { error } = await supabase
    .from("role_scopes" as any)
    .delete()
    .eq("user_id", userId)
    .eq("role", role);

  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Hook + mutation: unreviewed org-wide report-export access (#4656)
// ---------------------------------------------------------------------------

/**
 * Report-export-capable roles in this org that are still unscoped (implicit
 * org-wide access) and haven't yet been explicitly acknowledged by an admin.
 * Admin-only RPC -- see get_unreviewed_orgwide_report_roles in
 * supabase/migrations/20260811150000_report_export_scope_review.sql.
 */
export function useUnreviewedOrgWideReportRoles(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: ["unreviewed-orgwide-report-roles", organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "get_unreviewed_orgwide_report_roles" as never,
        { p_organization_id: organizationId } as never
      );

      if (error) throw error;
      return (data ?? []) as unknown as UnreviewedOrgWideReportRole[];
    },
  });
}

export async function acknowledgeOrgWideReportRole(params: {
  organizationId: string;
  userId: string;
  role: string;
}): Promise<void> {
  const { error } = await supabase.rpc("acknowledge_orgwide_report_role" as never, {
    p_organization_id: params.organizationId,
    p_user_id: params.userId,
    p_role: params.role,
  } as never);

  if (error) throw error;
}
